import {
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  Signal,
  viewChild,
  computed,
  TemplateRef,
  untracked
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { switchMap, take } from 'rxjs';

import {
  InsuranceReviewComponent,
  InsuranceVerificationFailedComponent
} from '@sondermind/features/account-edit';
import { AccountSetupContext, BasicInfoStartedType } from '@sondermind/constants';

import { ProposeATimeStore } from '@sondermind/data-access/client-facing/propose-a-time';
import {
  CurrentUser,
  ClientUserTags
} from '@sondermind/data-access/client-user';
import { SondermindEvent, SondermindEvents } from '@sondermind/analytics';
import { PatientInsuranceStore } from '@sondermind/data-access/client-insurance';

import { SegmentService } from '@sondermind/segment';
import { ClientUser } from '@sondermind/utilities/models-user-client';
import { AccountSetupService } from '@sondermind/utilities/services';
import { ICostEstimateContextAttributes } from '@sondermind/utilities/models-client-insurance';
import { Logger } from '@sondermind/datadog-logging';
import { FEATURE_FLAG_SERVICE, FeatureFlagKey, IFeatureFlagService } from '@sondermind/data-access/feature-flags';
import { FinancialAgreementTaskCardContext } from '@sondermind/features/client-facing/financial-agreement-task-card';

import { ProviderStore } from '@sondermind/data-access/client-portal-referrals';
import {
  ClientCostEstimationComponent,
  SelfPayCostEstimationComponent
} from '@sondermind/features/client-facing/cost-estimation';
import { AccountSetupStateMachine, SetupStep } from './services/account-setup-state-machine.service';
import {
  IInsuranceInfoSubmitted,
  SetupInsuranceInfoComponent
} from './components/setup-insurance-info/setup-insurance-info.component';
import { SetupBasicInfoComponent } from './components/setup-basic-info/setup-basic-info.component';
import { SetupEAPInfoComponent } from './components/setup-eap-info/setup-eap-info.component';
import { SetupPaymentInfoComponent } from './components/setup-payment-info/setup-payment-info.component';
import { SetupEbcgPaymentInfoComponent } from './components/setup-ebcg-payment-info/setup-ebcg-payment-info.component';

@Component({
  selector: 'client-account-setup',
  templateUrl: './account-setup.component.html',
  styleUrls: ['./account-setup.component.scss'],
  standalone: false,
  providers: [AccountSetupStateMachine]
})
export class AccountSetupComponent {
  readonly user = input.required<ClientUser>();
  readonly isMobile = input<boolean>(false);
  readonly isProposeATime = input<boolean>(false);
  readonly isRequestToSchedule = input<boolean>(false);
  readonly useInSessionRequest = input<boolean>(false);
  readonly processing = input<boolean>(false);
  readonly costEstimateContext = input.required<ICostEstimateContextAttributes>();
  readonly isInsuranceEmpty = input.required<boolean>();
  readonly hasSelfPayInsuranceRecord = input<boolean>(false);
  readonly fromDirectBooking = input<boolean>(false);
  readonly ebcgEmbraceProviderFromDirectBooking = input<boolean>(false);
  readonly providerNameFromDirectBooking = input<string>('');
  /** From parent (e.g. AccountSetupPage) when account setup runs outside direct booking. */
  readonly ebcgEmbraceProviderFromUpcomingAppointment = input<boolean>(false);
  readonly providerNameFromUpcomingAppointment = input<string>('');

  /** Only used when fromDirectBooking is true: pending appointment info for self-pay cost estimate */
  readonly providerPersonaProviderId = input<number>();
  readonly providerIsPsychiatrist = input<boolean>();
  readonly isTelehealthAppointment = input<boolean>();

  readonly progressBarValueUpdate = output<number>();
  readonly setupDataSubmit = output<void>();
  readonly setupExited = output<void>();
  readonly setupConfirmed = output<void>();

  readonly scrollAnchor = viewChild<ElementRef<HTMLElement>>('scrollAnchor');

  // Progress bar calculation constants
  private readonly progressBaseValue = 25;
  private readonly progressIncrement = 25;
  private readonly saasProgressIncrement = 50;
  private readonly sessionRequestBaseValue = 40;
  private readonly sessionRequestIncrement = 20;

  // Template reference for the current action button that parent can render
  readonly accountSetupActionButton =
    viewChild.required<TemplateRef<unknown>>('accountSetupActionButton');

  // ViewChild references for each setup step component
  readonly basicInfoComponent = viewChild<SetupBasicInfoComponent>('basicInfoComponent');
  readonly eapInfoComponent = viewChild<SetupEAPInfoComponent>('eapInfoComponent');
  readonly insuranceInfoComponent = viewChild<SetupInsuranceInfoComponent>('insuranceInfoComponent');
  readonly paymentInfoComponent = viewChild<SetupPaymentInfoComponent>('paymentInfoComponent');
  readonly ebcgPaymentInfoComponent = viewChild<SetupEbcgPaymentInfoComponent>('ebcgPaymentInfoComponent');
  readonly insuranceReviewComponent = viewChild<InsuranceReviewComponent>('insuranceReviewComponent');
  readonly insuranceVerificationFailedComponent =
    viewChild<InsuranceVerificationFailedComponent>('insuranceVerificationFailedComponent');
  readonly costEstimationComponent = viewChild<ClientCostEstimationComponent>('costEstimationComponent');
  readonly selfPayCostEstimationComponent =
    viewChild<SelfPayCostEstimationComponent>('selfPayCostEstimationComponent');

  /**
   * Computed signal that returns the current step's button template.
   * Used by parent component to render the appropriate action button.
   */
  readonly currentActionButtonTemplate = computed<TemplateRef<unknown> | null | undefined>(() => {
    // Read all viewChild signals upfront to ensure proper dependency tracking
    const basicInfo = this.basicInfoComponent();
    const eapInfo = this.eapInfoComponent();
    const insuranceInfo = this.insuranceInfoComponent();
    const paymentInfo = this.paymentInfoComponent();
    const ebcgPaymentInfo = this.ebcgPaymentInfoComponent();
    const insuranceReview = this.insuranceReviewComponent();
    const insuranceVerificationFailed = this.insuranceVerificationFailedComponent();
    const costEstimation = this.costEstimationComponent();
    const selfPayCostEstimation = this.selfPayCostEstimationComponent();

    // Now branch on step
    if (this.onBasicInfo()) {
      return basicInfo ? basicInfo.actionButtonTemplate() : null;
    }
    if (this.onEAP()) {
      return eapInfo ? eapInfo.actionButtonTemplate() : null;
    }
    if (this.onInsurance()) {
      return insuranceInfo ? insuranceInfo.actionButtonTemplate() : null;
    }
    if (this.onPayment()) {
      return paymentInfo ? paymentInfo.actionButtonTemplate() : null;
    }
    if (this.onEbcgPayment()) {
      // Hide action button while waiting for payment or when error occurred
      if (ebcgPaymentInfo?.waitingForPayment() || ebcgPaymentInfo?.paymentFailed()) {
        return null;
      }
      return ebcgPaymentInfo ? ebcgPaymentInfo.actionButtonTemplate() : null;
    }
    if (this.onInsuranceReview()) {
      return insuranceReview ? insuranceReview.actionButtonTemplate() : null;
    }
    if (this.onInsuranceVerificationFailed()) {
      return insuranceVerificationFailed ? insuranceVerificationFailed.actionButtonTemplate() : null;
    }
    if (this.onCostEstimation()) {
      if (this.isSelfPayCostEstimate()) {
        return selfPayCostEstimation ? selfPayCostEstimation.actionButtonTemplate() : null;
      }
      return costEstimation ? costEstimation.actionButtonTemplate() : null;
    }
    return null;
  });

  // Inject dependencies
  private readonly router = inject(Router);
  private readonly currentUser = inject(CurrentUser);
  private readonly proposeATimeStore = inject(ProposeATimeStore);
  private readonly accountSetupService = inject(AccountSetupService);
  private readonly segmentService = inject(SegmentService<SondermindEvents>);
  private readonly providerStore = inject(ProviderStore);
  private readonly patientInsuranceStore = inject(PatientInsuranceStore);
  private readonly logger = inject(Logger);
  private readonly stateMachine = inject(AccountSetupStateMachine);
  private readonly destroyRef = inject(DestroyRef);
  private readonly featureFlagService = inject<IFeatureFlagService>(FEATURE_FLAG_SERVICE);

  private readonly financialAgreementStepEnabled = toSignal(
    this.featureFlagService.isEnabled$(FeatureFlagKey.FINANCIAL_AGREEMENT_TASK),
    { initialValue: false }
  );

  protected readonly financialAgreementCardContext = FinancialAgreementTaskCardContext.ACCOUNT_SETUP;

  progressBarHeaderBasicInfo: string = 'Fill in a few details to get in touch';
  progressBarHeaderInsurance: string = 'Would you like to use any insurance benefits?';
  progressBarHeaderEAP: string = 'Would you like to use any Employee Assistance Program benefits?';
  progressBarHeaderPayment: string = 'Add your payment method';
  progressBarHeaderFinancialAgreement: string = 'Review our financial terms';

  // Step visibility signals are now delegated to the state machine
  readonly onBasicInfo: Signal<boolean> = this.stateMachine.onBasicInfo;
  readonly onInsurance: Signal<boolean> = this.stateMachine.onInsurance;
  readonly onEAP: Signal<boolean> = this.stateMachine.onEAP;
  readonly onPayment: Signal<boolean> = this.stateMachine.onPayment;
  readonly onEbcgPayment: Signal<boolean> = this.stateMachine.onEbcgPayment;
  readonly onFinancialAgreement: Signal<boolean> = this.stateMachine.onFinancialAgreement;
  readonly onCostEstimation: Signal<boolean> = this.stateMachine.onCostEstimation;

  // Insurance review steps are still managed locally (special case - not part of normal flow)
  readonly onInsuranceReview = signal<boolean>(false);
  readonly onInsuranceVerificationFailed = signal<boolean>(false);
  readonly isEAPReview = signal<boolean>(false);

  showPaymentFaq: boolean = false;
  basicInfoStartedType: BasicInfoStartedType = BasicInfoStartedType.INDEPENDENT;
  accountSetupType: AccountSetupContext = AccountSetupContext.INDEPENDENT;

  progressBarValue: number = 0;
  progressBarHeader: string = '';

  userHasSuccessfulMostRecentBv: boolean = false;

  // Local component state that gates payment-page determination and step rendering.
  private readonly isSaasInvited = signal<boolean>(false);
  readonly loadingSaasStatus = signal<boolean>(true);
  private readonly validForEbcgPaymentCollection = signal<boolean>(false);
  private readonly loadingInsuranceEligibility = signal<boolean>(false);
  private readonly ebcgEligibilityCheckStarted = signal<boolean>(false);
  private readonly isMultipayor = toSignal(this.currentUser.isMultipayor$, { initialValue: false });

  /**
   * Computed signal to check if we're still loading critical data for payment page determination.
   */
  readonly isLoadingPaymentPageData = computed(() => {
    if (this.loadingSaasStatus()) {
      return true;
    }

    // Check if EBCG eligibility data is still loading
    if (!this.ebcgEligibilityCheckStarted()) {
      return true;
    }
    if (this.loadingInsuranceEligibility()) {
      return true;
    }

    return false;
  });

  /**
   * Effective EBCG embrace provider: from input when in direct booking,
   * from upcoming appointment's provider when outside direct booking.
   */
  readonly ebcgEmbraceProvider = computed(() => (
    this.fromDirectBooking()
      ? this.ebcgEmbraceProviderFromDirectBooking()
      : this.ebcgEmbraceProviderFromUpcomingAppointment()
  ));

  /**
   * Effective provider name for EBCG payment screen: from input when in direct booking,
   * from upcoming appointment's provider when outside direct booking.
   */
  readonly providerName = computed(() => (
    this.fromDirectBooking()
      ? this.providerNameFromDirectBooking()
      : this.providerNameFromUpcomingAppointment()
  ));

  /**
   * True when the cost estimation step is shown for a self-pay client (no insurance)
   */
  readonly isSelfPayCostEstimate = computed(() => this.onCostEstimation() &&
    !this.stateMachine.insuranceSetupHadBvError() &&
    (this.stateMachine.selfPayJustSelected() || this.isInsuranceEmpty() === true));

  constructor() {
    // Initialize lifecycle: Wait for SAAS check to emit first value
    this.providerStore.isClientSaasInvited$().pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (saasInvited) => {
        // Set SAAS status
        this.isSaasInvited.set(saasInvited);

        // Check insurance eligibility for EBCG
        this.checkInsuranceEligibilityFromStore();

        this.loadingSaasStatus.set(false);

        // Now that flags are loaded, check account status
        this.checkAccountStatus();

        this.accountSetupService.clearContext();
        this.setAccountSetupContext();
        this.sendToSegment();
        this.checkUserBenefitVerification();
      },
      error: (err: unknown) => {
        // Fail open: clear the loading gate so the flow renders (defaults to non-SAAS)
        // instead of stranding the user on an indefinite spinner.
        this.logger.error('[AccountSetupComponent] SAAS status check failed', { err });
        this.loadingSaasStatus.set(false);
        this.checkInsuranceEligibilityFromStore();
      }
    });

    // React to user or insurance empty changes only (not every state machine mutation)
    effect(() => {
      // Track all input dependencies upfront to establish proper reactive boundaries
      // This prevents nested computed signals from causing feedback loops
      this.user();
      this.isInsuranceEmpty();
      // Read loading flags upfront so changes to them trigger re-evaluation
      this.loadingSaasStatus();
      this.ebcgEligibilityCheckStarted();
      this.loadingInsuranceEligibility();
      // Read shouldUseEbcgPayment dependencies upfront to ensure they're tracked
      this.ebcgEmbraceProvider();
      this.validForEbcgPaymentCollection();
      this.isMultipayor();
      this.financialAgreementStepEnabled();

      // Only check account status if all required data is loaded
      if (!this.isLoadingPaymentPageData()) {
        // Call untracked to prevent checkAccountStatus from adding state machine signals as dependencies
        untracked(() => {
          this.checkAccountStatus();
        });
      }
      // These methods read user() which is already tracked above
      untracked(() => {
        this.checkShowPaymentFaq();
        this.checkUserBenefitVerification();
      });
    });

    // Cleanup on destroy
    this.destroyRef.onDestroy(() => {
      this.accountSetupService.clearContext();
    });
  }

  setAccountSetupContext(): void {
    if (this.isProposeATime()) {
      this.accountSetupService.context = AccountSetupContext.PROPOSE_A_TIME;
      this.basicInfoStartedType = BasicInfoStartedType.PROPOSE_A_TIME;
      this.accountSetupType = AccountSetupContext.PROPOSE_A_TIME;
    } else if (this.useInSessionRequest()) {
      this.accountSetupService.context = AccountSetupContext.REQUEST_TO_SCHEDULE;
      this.basicInfoStartedType = BasicInfoStartedType.REQUEST_TO_SCHEDULE;
      this.accountSetupType = AccountSetupContext.REQUEST_TO_SCHEDULE;
    } else {
      this.accountSetupService.context = AccountSetupContext.INDEPENDENT;
      this.basicInfoStartedType = BasicInfoStartedType.INDEPENDENT;
      this.accountSetupType = AccountSetupContext.INDEPENDENT;
    }
  }

  sendToSegment(): void {
    if (!this.onBasicInfo()) return;

    this.segmentService.track(
      SondermindEvent.BasicInfoStarted,
      {
        acType: this.basicInfoStartedType,
        directBookingModal: this.fromDirectBooking()
      }
    ).catch(() => {});
  }

  /**
   * On submit of a setup step, tell parent.
   * reviewInsurance only comes on insurance submit, and will be true if BV fails
   */
  setupDataSubmitted(insuranceInfoSubmitted: IInsuranceInfoSubmitted = {
    reviewInsurance: false,
    verificationOutage: false
  }): void {
    if (insuranceInfoSubmitted.exitSetup) {
      this.stateMachine.updateState({ insuranceJustRejected: true });
      this.setupExited.emit();
      return;
    }

    // Snapshot the step being submitted FROM before any updateState call. The step signals are
    // computed off the state machine, so a flag set below immediately advances currentStep — reading
    // them afterwards would report the *next* step and credit this submission to the wrong step.
    const submittedFromBasicInfo = this.onBasicInfo();
    const submittedFromInsurance = this.onInsurance();

    if (insuranceInfoSubmitted.bvErrorEncountered === true) {
      this.stateMachine.updateState({ insuranceSetupHadBvError: true });
    }

    // If we're coming from basic info step, mark that attestations were just submitted successfully
    // This handles the race condition where backend attestation flags aren't updated immediately
    // Note: This event only fires when attestations actually succeed (not on failure)
    if (submittedFromBasicInfo) {
      this.stateMachine.updateState({ attestationsJustSucceeded: true });
    }

    // If we're coming from insurance step and no BV error, mark that insurance was just submitted successfully
    if (submittedFromInsurance && !insuranceInfoSubmitted.reviewInsurance) {
      this.stateMachine.updateState({ insuranceJustSucceeded: true });
      // Refresh insurance list to get updated validForEbcgPaymentCollection values
      // This ensures the correct payment page is shown after insurance is added
      this.checkInsuranceEligibilityFromStore();
    }

    // If self-pay was just selected, mark that to prevent showing insurance step again
    // This handles the race condition where backend user flags aren't updated immediately after self-pay
    if (insuranceInfoSubmitted.selfPaySelected) {
      this.stateMachine.updateState({ selfPayJustSelected: true });
    }

    if (insuranceInfoSubmitted.payorDowntimeAccepted) {
      this.stateMachine.updateState({ payorDowntimeJustAccepted: true });
    }

    if (insuranceInfoSubmitted.reviewInsurance) {
      if (!insuranceInfoSubmitted.verificationOutage) {
        this.onInsuranceReview.set(true);
        this.onInsuranceVerificationFailed.set(false);
      } else {
        this.onInsuranceReview.set(false);
        this.onInsuranceVerificationFailed.set(true);
      }

      this.scrollToTop();
    } else {
      // If not showing review screen, emit to trigger next step
      this.setupDataSubmit.emit();
    }
  }

  /**
   * Push the current user and derived config into the state machine so `currentStep()`
   * reflects the latest inputs. Single source for the config mapping.
   */
  private syncStateMachine(): void {
    this.stateMachine.setUser(this.user());
    this.stateMachine.setConfig({
      isProposeATime: this.isProposeATime(),
      isRequestToSchedule: this.isRequestToSchedule(),
      useInSessionRequest: this.useInSessionRequest(),
      isSaasInvited: this.isSaasInvited(),
      isInsuranceEmpty: this.isInsuranceEmpty(),
      hasSelfPayInsuranceRecord: this.hasSelfPayInsuranceRecord(),
      shouldUseEbcgPayment: this.shouldUseEbcgPayment(),
      financialAgreementStepEnabled: this.financialAgreementStepEnabled()
    });
  }

  checkAccountStatus(): void {
    // Wait for user input to be available (required input might not be set during constructor initialization)
    // Use try-catch because accessing a required input before it's set throws NG0950
    let currentUser: ClientUser | undefined;
    try {
      currentUser = this.user();
    } catch {
      // Input not yet available during initialization
      return;
    }

    if (!currentUser) {
      return;
    }

    // Wait for all critical data to load before determining payment page
    if (this.isLoadingPaymentPageData()) {
      return;
    }

    // Update state machine with current user and config
    this.syncStateMachine();

    // Reset insurance review steps (these are not part of the normal flow)
    this.onInsuranceReview.set(false);
    this.onInsuranceVerificationFailed.set(false);

    // Get current step from state machine
    const currentStep = this.stateMachine.currentStep();

    // Handle completion cases
    if (currentStep === SetupStep.COMPLETE) {
      if (this.isProposeATime()) {
        this.proposeATimeStore.updateInPATAccountSetupComplete(true);
        this.proposeATimeStore.setHideBackButton(false);
      }

      if (!this.useInSessionRequest() && !this.isProposeATime()) {
        if (this.fromDirectBooking()) {
          this.setupConfirmed.emit();
        } else {
          this.router.navigate(['/profile/setup-complete']);
        }
      }

      // Reset all flags when complete
      this.stateMachine.resetRaceFlags();
      return;
    }

    // Update progress bar header based on step
    switch (currentStep) {
      case SetupStep.BASIC_INFO:
        this.progressBarHeader = this.progressBarHeaderBasicInfo;
        this.stateMachine.resetFlagsForStepTransition(SetupStep.BASIC_INFO);
        break;
      case SetupStep.EAP:
        this.progressBarHeader = this.progressBarHeaderEAP;
        this.stateMachine.resetFlagsForStepTransition(SetupStep.EAP);
        break;
      case SetupStep.INSURANCE:
        this.progressBarHeader = this.progressBarHeaderInsurance;
        this.stateMachine.resetFlagsForStepTransition(SetupStep.INSURANCE);
        break;
      case SetupStep.PAYMENT:
      case SetupStep.EBCG_PAYMENT:
        this.progressBarHeader = this.progressBarHeaderPayment;
        break;
      case SetupStep.FINANCIAL_AGREEMENT:
        this.progressBarHeader = this.progressBarHeaderFinancialAgreement;
        break;
      case SetupStep.COST_ESTIMATION:
      case SetupStep.INSURANCE_REVIEW:
      case SetupStep.INSURANCE_VERIFICATION_FAILED:
        // No progress bar header change for these steps
        break;
      default:
        // All step cases handled above (COMPLETE returns early)
        break;
    }

    // Safety check: if no step is showing, log error to diagnose logic bugs
    const hasActiveStep = this.onBasicInfo() || this.onEAP() || this.onInsurance() ||
      this.onInsuranceReview() || this.onInsuranceVerificationFailed() ||
      this.onCostEstimation() || this.onPayment() || this.onEbcgPayment() || this.onFinancialAgreement();

    if (!hasActiveStep) {
      this.logger.error('Account setup rendering blank page: no active step but account incomplete', {
        user: {
          personaId: this.user()?.personaId,
          needsSetup: this.user()?.needsSetup,
          requiresProfileVerification: this.user()?.requiresProfileVerification,
          requiresInsuranceInfo: this.user()?.requiresInsuranceInfo,
          requiresCreditCard: this.user()?.requiresCreditCard,
          requiresFinancialAgreement: this.user()?.requiresFinancialAgreement
        },
        stateMachine: {
          currentStep,
          state: this.stateMachine.getState()
        }
      });
    }

    // flag visit to route, duplicate calls are handled by back end so that only first visit is recorded
    this.currentUser.setHasVisitedAccountSetup(this.user().personaId);

    this.updateProgressBarValue();
    this.scrollToTop();
  }

  /**
   * Calculate progress bar value for setup progress.
   * NOTE: does not mathematically account well for when a user has completed some steps, leaves, and returns
   * OR when user does not initially require certain information to be complete such as VA clients with payment info
   */
  updateProgressBarValue(): void {
    // Ensure state machine is synced with current user/config state
    this.syncStateMachine();

    let progressBarValue: number;
    let progressBarIncrement: number;

    const currentStep = this.stateMachine.currentStep();
    // Determine which steps are complete based on whether we've moved past them in the flow
    const isBasicInfoComplete =
      currentStep === SetupStep.EAP ||
      currentStep === SetupStep.INSURANCE ||
      currentStep === SetupStep.COST_ESTIMATION ||
      currentStep === SetupStep.PAYMENT ||
      currentStep === SetupStep.EBCG_PAYMENT ||
      currentStep === SetupStep.FINANCIAL_AGREEMENT ||
      currentStep === SetupStep.COMPLETE;

    const isInsuranceOrEAPComplete =
      currentStep === SetupStep.COST_ESTIMATION ||
      currentStep === SetupStep.PAYMENT ||
      currentStep === SetupStep.EBCG_PAYMENT ||
      currentStep === SetupStep.FINANCIAL_AGREEMENT ||
      currentStep === SetupStep.COMPLETE;

    const isPaymentComplete = currentStep === SetupStep.COMPLETE;

    if (this.isSaasInvited()) {
      // For SAAS clients: only 2 steps (basic info + payment)
      progressBarValue = this.progressBaseValue;
      progressBarIncrement = this.saasProgressIncrement;

      if (isBasicInfoComplete) {
        progressBarValue += progressBarIncrement;
        this.proposeATimeStore.setBasicInfoSubmitted(true);
      }

      if (isPaymentComplete) {
        progressBarValue += progressBarIncrement;
      }
    } else {
      // Original logic for non-SAAS clients
      progressBarValue = this.useInSessionRequest() ? this.sessionRequestBaseValue : this.progressBaseValue;
      progressBarIncrement = this.useInSessionRequest() ? this.sessionRequestIncrement : this.progressIncrement;

      if (isBasicInfoComplete) {
        progressBarValue += progressBarIncrement;
        this.proposeATimeStore.setBasicInfoSubmitted(true);
      }

      if (isInsuranceOrEAPComplete) {
        progressBarValue += progressBarIncrement;
      }

      if (isPaymentComplete) {
        progressBarValue += progressBarIncrement;
      }
    }

    if (this.useInSessionRequest() || this.fromDirectBooking()) {
      this.progressBarValueUpdate.emit(progressBarValue);
    } else {
      this.progressBarValue = progressBarValue;
    }
  }

  /**
   * Used ONLY in case the client selected EAP and Insurance in match flow
   * but declined to fill out the EAP form during setup.
   * Forces transition to insurance step by clearing EAP requirement.
   */
  eapToInsurance(): void {
    // Declining the EAP form persists nothing to the backend, so hasEapPref/requiresInsuranceInfo
    // stay set. Flag the decline so the state machine skips EAP and advances to insurance.
    this.stateMachine.updateState({ eapJustDeclined: true });
    this.onInsuranceReview.set(false);
    this.onInsuranceVerificationFailed.set(false);
    this.progressBarHeader = this.progressBarHeaderInsurance;
    this.checkAccountStatus();
  }

  /**
   * After each form is completed, return to top of screen.
   */
  scrollToTop(): void {
    setTimeout(() => {
      this.scrollAnchor()?.nativeElement?.scrollIntoView();
    }, 0);
  }

  /**
   * Do not show payment FAQ data for psychiatry or va clients because it is inaccurate for those clients.
   * Check for experiment text to show for PE-17684 ABC testing.
   */
  checkShowPaymentFaq(): void {
    try {
      this.showPaymentFaq = !(this.user()?.tags?.includes(ClientUserTags.VA_CLIENT)
        || this.user()?.tags?.includes(ClientUserTags.PSYCHIATRY_CLIENT));
    } catch {
      // Input not yet available during initialization
      this.showPaymentFaq = true;
    }
  }

  onCostEstimationReviewed(): void {
    this.stateMachine.updateState({ costEstimationReviewed: true });

    // If payment is required, transition locally (instant, no reload needed)
    // The state machine already knows to show PAYMENT or EBCG_PAYMENT from costEstimationReviewed flag
    if (this.missingFinancialInfo()) {
      this.checkAccountStatus();
    } else {
      // No payment step required: emit so parent reloads user and completes setup
      this.setupDataSubmit.emit();
    }

    this.scrollToTop();
  }

  /**
   * Check if client requires payment (credit card or financial agreement).
   * Used to determine if payment step is needed after cost estimation.
   */
  private missingFinancialInfo(): boolean {
    return this.user()?.requiresCreditCard === true ||
           this.user()?.requiresFinancialAgreement === true;
  }

  /**
   * Check if client's insurance is valid for EBCG payment collection using PatientInsuranceStore
   */
  checkInsuranceEligibilityFromStore(): void {
    this.ebcgEligibilityCheckStarted.set(true);
    this.loadingInsuranceEligibility.set(true);

    this.patientInsuranceStore.loadPatientInsuranceList$()
      .pipe(
        switchMap(() => this.patientInsuranceStore.fullInsuranceList$),
        take(1), // Complete after first emission to prevent subscription leak across multiple calls
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (insurances) => {
          const hasEbcgInsurance = (insurances ?? []).some(
            (insurance) => insurance.providerNetworkCompany?.validForEbcgPaymentCollection === true
          );

          this.validForEbcgPaymentCollection.set(hasEbcgInsurance);
          this.loadingInsuranceEligibility.set(false);

          // Re-check account status now that eligibility is loaded
          this.checkAccountStatus();
        },
        error: (err: unknown) => {
          // If there's an error, assume not valid and fall back to regular payment.
          // Log the error. This failure silently changes payment-page routing (EBCG → regular).
          this.logger.error(
            '[AccountSetupComponent] insurance eligibility load failed; defaulting to regular payment',
            { err }
          );
          this.validForEbcgPaymentCollection.set(false);
          this.loadingInsuranceEligibility.set(false);

          // TODO: Add user-facing error indication via banner or toast when eligibility check fails
          // to prevent users from being confused why they see regular payment instead of EBCG.
          // Consider: show a banner on the payment step saying "We couldn't verify your insurance
          // eligibility. Please contact support if you have questions about your payment options."

          // Re-check account status even on error
          this.checkAccountStatus();
        }
      });
  }

  /**
   * Determine if the user should see the EBCG payment screen instead of the regular payment screen.
   * Requires provider to be an EBCG Embrace provider,
   * client to have valid insurance for EBCG payment collection, client to be EBCG-eligible by creation date,
   * and client to not be multipayor (multipayor clients are billed through Cigna/Centivo, not EBCG).
   */
  readonly shouldUseEbcgPayment = computed(() => this.ebcgEmbraceProvider() === true &&
    this.validForEbcgPaymentCollection() &&
    !this.isMultipayor() &&
    this.user()?.clientEbcgEligibleByCreationDate === true);

  private checkUserBenefitVerification(): void {
    try {
      this.userHasSuccessfulMostRecentBv = !!this.user()?.hasSuccessfulMostRecentBv;
    } catch {
      // Input not yet available during initialization
      this.userHasSuccessfulMostRecentBv = false;
    }
  }

  onInsuranceInfoSubmitted(event: IInsuranceInfoSubmitted): void {
    this.setupDataSubmitted(event);
  }
}
